import Vue from 'vue';
import config from 'config';
import issuesService from '@mobile/services/issue';
import { eventBus } from '@/eventBus';

const getDefaultState = () => ({
    queue: [],
    issues: [],
    next_page_url: null,
    prev_page_url: null,
    loading: true,
    initial_loading: true,
    filterOptions: {
        assignees: [],
        sites: [],
    },
});

const issuesURL = `${config.apiUrl}/${config.apiVersion}/issues`;

export default {
    namespaced: true,

    state: getDefaultState(),

    getters: {
        issues: (state) => state.issues,
        isInitialLoading: (state) => state.initial_loading,
        isLoading: (state) => state.loading,
        hasNextPage: (state) => state.next_page_url,
        filterOptions: (state) => state.filterOptions,
    },

    actions: {
        raiseIssuesScreenCreated({ commit }) {
            commit('loadingStarted');

            return issuesService.getFilterOptions()
                .then((filterOptionsResult) => {
                    commit('filterOptionsFetched', filterOptionsResult);
                    commit('loadingFinished');
                    eventBus.$emit('loaded');
                }, (error) => {
                    eventBus.$emit('server-error', error);
                });
        },

        editIssuesScreenCreated({ commit }) {
            commit('initialLoadingStarted');
            commit('loadingStarted');

            return issuesService.getFilterOptions()
                .then((filterOptionsResult) => {
                    commit('filterOptionsFetched', filterOptionsResult);
                    commit('initialLoadingFinished');
                    commit('loadingFinished');
                    eventBus.$emit('loaded');
                }, (error) => {
                    eventBus.$emit('server-error', error);
                });
        },

        issuesScreenCreated({ commit }, { filters = {} }) {
            commit('issuesReseted');
            commit('initialLoadingStarted');
            commit('loadingStarted');

            return Promise.all([
                issuesService.getAll(issuesURL, filters),
                issuesService.getFilterOptions(),
            ]).then(([issuesResult, filterOptionsResult]) => {
                commit('issuesFetched', issuesResult);
                commit('filterOptionsFetched', filterOptionsResult);
                commit('initialLoadingFinished');
                commit('loadingFinished');
                eventBus.$emit('loaded');
            }, (error) => {
                eventBus.$emit('server-error', error);
            });
        },

        issueScreenRefreshed({ commit }, filters = {}) {
            commit('initialLoadingStarted');
            eventBus.$emit('loading');

            return issuesService.getAll(issuesURL, filters)
                .then((issuesResult) => {
                    commit('issuesReseted', issuesResult);
                    commit('issuesFetched', issuesResult);
                    commit('initialLoadingFinished');
                    eventBus.$emit('loaded');
                }, (error) => {
                    eventBus.$emit('server-error', error);
                });
        },

        loadMoreButtonClicked({ state, commit }, { filters = {} }) {
            commit('loadingStarted');

            return issuesService.getAll(state.next_page_url, filters)
                .then((result) => {
                    commit('issuesFetched', result);
                    commit('loadingFinished');
                    eventBus.$emit('loaded');
                }, (error) => {
                    eventBus.$emit('server-error', error);
                });
        },

        filterRequested({ commit }, { filters = {} }) {
            commit('loadingStarted');

            issuesService.getAll(issuesURL, filters)
                .then((result) => {
                    commit('issuesFiltered', result);
                    commit('loadingFinished');
                }, (error) => {
                    eventBus.$emit('server-error', error);
                });
        },

        async issueRaised({ commit }, payload) {
            commit('loadingStarted');

            try {
                const result = issuesService.raise(payload);
                commit('loadingFinished');
                commit('issueRaised', result);
                return Promise.resolve(result);
            } catch (error) {
                commit('loadingFinished');
                return Promise.reject(error);
            }
        },

        async issueEdited({ commit }, { id, form }) {
            commit('loadingStarted');

            try {
                const result = await issuesService.edit(id, form);
                commit('loadingFinished');
                commit('issueEdited', result);
                return Promise.resolve(result);
            } catch (error) {
                commit('loadingFinished');
                return Promise.reject(error);
            }
        },
    },

    mutations: {
        reset(state) {
            Object.assign(state, getDefaultState());
        },

        issuesReseted(state) {
            Vue.set(state, 'issues', []);
        },

        initialLoadingStarted(state) {
            Vue.set(state, 'initial_loading', true);
        },

        initialLoadingFinished(state) {
            Vue.set(state, 'initial_loading', false);
        },

        loadingStarted(state) {
            Vue.set(state, 'loading', true);
        },

        loadingFinished(state) {
            Vue.set(state, 'loading', false);
            eventBus.$emit('loaded');
        },

        // eslint-disable-next-line camelcase
        issuesFetched(state, { resources = [], prev_page_url, next_page_url }) {
            Vue.set(state, 'issues', [...state.issues, ...resources]);
            Vue.set(state, 'prev_page_url', prev_page_url);
            Vue.set(state, 'next_page_url', next_page_url);
        },

        // eslint-disable-next-line camelcase
        issuesFiltered(state, { resources = [], prev_page_url, next_page_url }) {
            Vue.set(state, 'issues', [...resources]);
            Vue.set(state, 'prev_page_url', prev_page_url);
            Vue.set(state, 'next_page_url', next_page_url);
        },

        filterOptionsFetched(state, { assignees, sites }) {
            Vue.set(state.filterOptions, 'assignees', assignees);
            Vue.set(state.filterOptions, 'sites', sites);
        },

        issueRaised(state, payload) {
            Vue.set(state, 'issues', [...state.issues, payload]);
        },

        issueEdited(state, payload) {
            const index = state.issues.findIndex((issue) => issue.id === payload.id);
            Vue.set(state.issues, index, payload);
        },
    },
};
